// MyConsumerAuth.jsx - ENHANCED with better error handling and debugging

import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import { toast } from "react-toastify";

const MyConsumerAuth = () => {
  const { consumer_id } = useParams();
  const [auths, setAuths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consumerName, setConsumerName] = useState("");
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const role = user?.role?.toLowerCase();
  const isJobCoach = user?.role?.toLowerCase() === "job coach";
  const tenantType = user?.tenant_type?.toLowerCase();

  useEffect(() => {
    const fetchAuths = async () => {
      try {
        console.log("ðŸ” DEBUG: Fetching auths with context:", {
          consumer_id,
          user_tenant_id: user.tenant_id,
          user_tenant_type: tenantType,
          user_role: role,
          user_counselor_id: user.counselor_id,
        });

        // Use secureAxios for all API calls
        const res = await secureAxios.get(
          `/api/my-consumers/${consumer_id}/auths`,
          {
            params: {
              tenant_id: user.tenant_id,
            },
          }
        );
        setAuths(res.data);

        // Get consumer name for audit logging
        const consumerRes = await secureAxios.get(
          `/api/my-consumers/${consumer_id}/info`,
          {
            params: {
              tenant_id: user.tenant_id,
            },
          }
        );
        setConsumerName(consumerRes.data.name);

        // Enhanced audit logging with customer-vendor context
        await auditApi.logActionSafe(
          "VIEW_CONSUMER_AUTHORIZATIONS_DETAIL",
          "AUTHORIZATION",
          consumer_id,
          {
            consumer_name: consumerRes.data.name,
            authorization_count: res.data.length,
            total_authorized_hours: res.data.reduce(
              (sum, a) => sum + (parseFloat(a.authorization_hours) || 0),
              0
            ),
            total_used_hours: res.data.reduce(
              (sum, a) => sum + (parseFloat(a.total_hours) || 0),
              0
            ),
            contains_phi: true,
            user_tenant_type: tenantType,
            access_model: "customer_vendor_relationship",
            cross_tenant_access: tenantType === "customer",
          }
        );
      } catch (err) {
        console.error("âŒ Failed to fetch authorization summary:", err);

        // Enhanced error handling
        if (err.response?.status === 403) {
          toast.error(
            "Access denied. You may not have permission to view this consumer's authorizations."
          );
        } else if (err.response?.status === 404) {
          toast.error("Consumer not found or no authorizations available.");
        } else {
          toast.error("Failed to load authorizations. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAuths();
  }, [consumer_id, tenantType, user.tenant_id]);

  // âœ… ENHANCED handleViewDocs with comprehensive error handling and debugging
  // const handleViewDocs = async (auth_id, auth_number) => {
  //   try {
  //     console.log("ðŸ” DEBUG: handleViewDocs starting with context:", {
  //       authorization_id: auth_id,
  //       authorization_number: auth_number,
  //       consumer_id: consumer_id,
  //       consumer_name: consumerName,
  //       user_tenant_type: tenantType,
  //       user_role: role,
  //       user_tenant_id: user.tenant_id,
  //       user_counselor_id: user.counselor_id,
  //     });

  //     // Enhanced audit logging for document access attempt
  //     await auditApi.logActionSafe(
  //       "VIEW_AUTHORIZATION_DOCUMENTS",
  //       "AUTHORIZATION",
  //       auth_id,
  //       {
  //         consumer_id: consumer_id,
  //         consumer_name: consumerName,
  //         authorization_number: auth_number,
  //         contains_phi: true,
  //         user_tenant_type: tenantType,
  //         access_model: "customer_vendor_cross_tenant",
  //         source_component: "MyConsumerAuth",
  //       }
  //     );

  //     // âœ… PRE-CHECK ACCESS before navigation to avoid redirect to dashboard
  //     const accessCheckParams = {
  //       tenant_id: user.tenant_id,
  //       authorization_id: auth_id,
  //     };

  //     console.log(
  //       "ðŸ” DEBUG: Pre-checking document access with params:",
  //       accessCheckParams
  //     );

  //     try {
  //       // Make a test request to see if we have access
  //       const testResponse = await secureAxios.get("/api/documents", {
  //         params: accessCheckParams,
  //       });

  //       console.log(
  //         "-"
  //       );
  //       console.log("ðŸ“„ DEBUG: Found", testResponse.data.length, "documents");

  //       // Navigate to documents - access is confirmed
  //       navigate(
  //         `/crm/documents?consumer_id=${consumer_id}&authorization_id=${auth_id}`
  //       );
  //     } catch (accessError) {
  //       console.error("âŒ DEBUG: Document access denied:", {
  //         status: accessError.response?.status,
  //         error_data: accessError.response?.data,
  //         authorization_id: auth_id,
  //         consumer_id: consumer_id,
  //       });

  //       // âœ… ENHANCED ERROR HANDLING based on error type
  //       if (accessError.response?.status === 403) {
  //         const errorDetails = accessError.response?.data;

  //         if (errorDetails?.code === "HIPAA_ACCESS_DENIED") {
  //           console.error("ðŸ”’ HIPAA Access Denied:", errorDetails);

  //           // Show user-friendly error message based on their role and tenant type
  //           let errorMessage = `You don't have permission to view documents for ${consumerName}.`;

  //           if (tenantType === "vendor") {
  //             if (role === "job coach") {
  //               errorMessage +=
  //                 "\n\nJob coaches can only view documents for consumers they are assigned to. Please check with your supervisor if you should have access to this consumer.";
  //             } else if (role !== "admin") {
  //               errorMessage +=
  //                 "\n\nOnly vendor admins and assigned job coaches can view documents.";
  //             }
  //           } else if (tenantType === "customer") {
  //             if (role === "counselor") {
  //               errorMessage +=
  //                 "\n\nCounselors can only view documents for consumers they are assigned to. Please check your consumer assignments.";
  //             } else if (role !== "admin") {
  //               errorMessage +=
  //                 "\n\nOnly customer admins and assigned counselors can view documents.";
  //             }
  //           }

  //           // Add access rules explanation if available
  //           if (errorDetails.access_rules) {
  //             errorMessage += "\n\nAccess Rules:";
  //             if (errorDetails.access_rules.vendor_users) {
  //               errorMessage += `\nVendor Users: ${errorDetails.access_rules.vendor_users}`;
  //             }
  //             if (errorDetails.access_rules.customer_users) {
  //               errorMessage += `\nCustomer Users: ${errorDetails.access_rules.customer_users}`;
  //             }
  //           }

  //           // âœ… DEBUG INFO for developers (non-production)
  //           if (process.env.NODE_ENV === "development") {
  //             errorMessage += `\n\nDEBUG INFO:`;
  //             errorMessage += `\nYour Tenant Type: ${tenantType}`;
  //             errorMessage += `\nYour Role: ${role}`;
  //             errorMessage += `\nYour Counselor ID: ${user.counselor_id}`;
  //             if (errorDetails.context) {
  //               errorMessage += `\nRequired Counselor ID: ${errorDetails.context.consumer_counselor_id}`;
  //               errorMessage += `\nIs Assigned Counselor: ${errorDetails.context.isAssignedCounselor}`;
  //               errorMessage += `\nIs Customer Tenant: ${errorDetails.context.isCustomerTenant}`;
  //               errorMessage += `\nIs Vendor Tenant: ${errorDetails.context.isVendorTenant}`;
  //             }
  //           }

  //           toast.error(errorMessage, {
  //             autoClose: 15000, // Longer duration for detailed error
  //             style: { whiteSpace: "pre-line" },
  //           });
  //         } else {
  //           // Generic 403 error
  //           toast.error(
  //             `Access denied to view documents for authorization ${auth_number}. You may not have the necessary permissions.`,
  //             { autoClose: 6000 }
  //           );
  //         }
  //       } else if (accessError.response?.status === 404) {
  //         toast.error(
  //           `Authorization ${auth_number} not found or has no documents.`,
  //           { autoClose: 5000 }
  //         );
  //       } else if (accessError.response?.status === 401) {
  //         toast.error("Your session has expired. Please log in again.", {
  //           autoClose: 3000,
  //         });
  //         // Let secureAxios handle the redirect
  //       } else {
  //         // Generic error
  //         console.error("âŒ DEBUG: Unexpected error:", accessError);

  //         toast.error(
  //           `Unable to access documents for authorization ${auth_number}. Please try again or contact support.`,
  //           { autoClose: 5000 }
  //         );
  //       }

  //       // âœ… CRITICAL: Do NOT navigate on access errors
  //       // This prevents the redirect to dashboard
  //       return;
  //     }
  //   } catch (error) {
  //     console.error("âŒ DEBUG: Error in handleViewDocs:", error);

  //     // Even if audit logging fails, still attempt access check
  //     try {
  //       const accessCheckParams = {
  //         tenant_id: user.tenant_id,
  //         authorization_id: auth_id,
  //       };

  //       await secureAxios.get("/api/documents", {
  //         params: accessCheckParams,
  //       });

  //       // If access check succeeds, navigate
  //       navigate(
  //         `/crm/documents?consumer_id=${consumer_id}&authorization_id=${auth_id}`
  //       );
  //     } catch (fallbackError) {
  //       console.error(
  //         "âŒ DEBUG: Fallback access check also failed:",
  //         fallbackError
  //       );
  //       toast.error(
  //         "Unable to access documents. Please try again or contact support.",
  //         { autoClose: 5000 }
  //       );
  //     }
  //   }
  // };

  // ... (keep all the existing utility functions and JSX render logic unchanged)

  // ðŸ”§ FIXED MyConsumerAuth.jsx - handleViewDocs method
  // This removes the problematic pre-check and lets DocumentList handle access control

  const handleViewDocs = async (auth_id, auth_number) => {
    try {
      console.log("ðŸ” DEBUG: handleViewDocs starting with context:", {
        authorization_id: auth_id,
        authorization_number: auth_number,
        consumer_id: consumer_id,
        consumer_name: consumerName,
        user_tenant_type: tenantType,
        user_role: role,
        user_tenant_id: user.tenant_id,
        user_counselor_id: user.counselor_id,
      });

      // Enhanced audit logging
      await auditApi.logActionSafe(
        "VIEW_AUTHORIZATION_DOCUMENTS",
        "AUTHORIZATION",
        auth_id,
        {
          consumer_id: consumer_id,
          consumer_name: consumerName,
          authorization_number: auth_number,
          contains_phi: true,
          user_tenant_type: tenantType,
          access_model: "customer_vendor_cross_tenant",
          source_component: "MyConsumerAuth",
        }
      );

      console.log(
        "-"
      );

      // ðŸ” ENHANCED DEBUG: Log the exact URL being navigated to
      const navigationUrl = `/crm/documents?consumer_id=${consumer_id}&authorization_id=${auth_id}`;
      console.log("ðŸ”— DEBUG: Navigating to URL:", navigationUrl);
      console.log("ðŸ”— DEBUG: Current location:", window.location.href);

      navigate(navigationUrl);

      // ðŸ” DEBUG: Log after navigation attempt
      setTimeout(() => {
        console.log(
          "ðŸ”— DEBUG: Location after navigation:",
          window.location.href
        );
      }, 1000);
    } catch (error) {
      console.error("âŒ DEBUG: Error in handleViewDocs:", error);

      // Even if audit logging fails, still attempt navigation
      console.log("âš ï¸ DEBUG: Proceeding with navigation despite audit error");
      const fallbackUrl = `/crm/documents?consumer_id=${consumer_id}&authorization_id=${auth_id}`;
      console.log("ðŸ”— DEBUG: Fallback navigation to:", fallbackUrl);
      navigate(fallbackUrl);
    }
  };

  const formatFloat = (val) =>
    val !== null && val !== undefined ? parseFloat(val).toFixed(1) : "-";

  const formatDate = (dateString) => {
    // Handle null, undefined, empty, or mojibake values
    if (!dateString || 
        dateString === "-" || 
        dateString === "--" ||
        dateString.includes("â€") ||  // Common mojibake pattern
        dateString.includes("\u00e2\u0080") ||  // UTF-8 mojibake
        dateString.length < 4) {  // Too short to be a valid date
      return "-";
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("en-US");
    } catch (err) {
      return "-";
    }
  };

  const calculateUtilization = (authorization) => {
    const authorizedNum = parseFloat(authorization.authorization_hours) || 0;
    if (authorizedNum === 0) return 0;

    let usedNum = 0;
    if (authorization.auth_type === "mileage") {
      usedNum = parseFloat(authorization.total_miles) || 0;
    } else {
      usedNum = parseFloat(authorization.total_hours) || 0;
    }

    const percentage = (usedNum / authorizedNum) * 100;
    
    // For flat rate services, cap utilization at 100% since we only bill the authorized units
    if (authorization.flat_rate && percentage > 100) {
      return 100;
    }
    
    return isNaN(percentage) ? 0 : percentage;
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return "text-danger";
    if (percentage >= 75) return "text-warning";
    return "text-success";
  };

  const formatUtilization = (percentage) => {
    if (isNaN(percentage) || !isFinite(percentage)) return "0";
    return percentage.toFixed(1);
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "200px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // âœ… ENHANCED DEBUG INFO in development
  if (process.env.NODE_ENV === "development") {
    console.log("ðŸ” DEBUG: MyConsumerAuth render context:", {
      consumer_id,
      consumerName,
      tenantType,
      role,
      authCount: auths.length,
      user_tenant_id: user.tenant_id,
      user_counselor_id: user.counselor_id,
    });
  }

  return (
    <div className="container-fluid px-3 px-sm-4 px-lg-5">
      {/* HIPAA Compliance Notice */}
      <div className="alert alert-info d-flex align-items-start mb-3">
        <i className="bi bi-shield-lock me-2 flex-shrink-0"></i>
        <div className="small">
          <strong>HIPAA Notice:</strong> This authorization summary contains
          Protected Health Information (PHI). All access is logged and
          monitored.{" "}
          {tenantType === "customer" &&
            "As a customer tenant user, you have cross-tenant access to view work performed by vendor tenants on your behalf."}
        </div>
      </div>

      {/* Header Section */}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-3">
        <div className="mb-2 mb-lg-0">
          <h2 className="fs-3 fs-lg-2 mb-1">Authorization Summary</h2>
          {consumerName && (
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
              <h5 className="text-muted mb-0 d-flex align-items-center">
                <i className="bi bi-person-circle me-2"></i>
                <span>{consumerName}</span>
              </h5>
              <div>
                {tenantType === "customer" && (
                  <small className="badge bg-info">Customer Access</small>
                )}
                {tenantType === "vendor" && (
                  <small className="badge bg-success">Vendor Access</small>
                )}
              </div>
            </div>
          )}
        </div>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => navigate(`/crm/my-consumers/${consumer_id}/info`)}
        >
          <i className="bi bi-arrow-left me-1"></i>
          <span className="d-none d-sm-inline">Back to Consumer Info</span>
          <span className="d-inline d-sm-none">Back</span>
        </button>
      </div>

      {/* Summary Stats */}
      <div className="row g-2 g-md-3 mb-4">
        <div className="col-6 col-lg-3">
          <div className="card h-100 border-primary">
            <div className="card-body text-center py-3">
              <div className="text-primary mb-2">
                <i className="bi bi-file-text fs-2"></i>
              </div>
              <h6 className="card-subtitle mb-2 text-muted small">
                Total Authorizations
              </h6>
              <h4 className="card-title mb-0 fs-5 fs-md-4 text-primary fw-bold">
                {auths.filter((a) => !a.complete).length}
              </h4>
              {auths.filter((a) => a.complete).length > 0 && (
                <small
                  className="text-muted d-block"
                  style={{ fontSize: "0.7rem" }}
                >
                  ({auths.filter((a) => a.complete).length} complete)
                </small>
              )}
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="card h-100 border-info">
            <div className="card-body text-center py-3">
              <div className="text-info mb-2">
                <i className="bi bi-clock fs-2"></i>
              </div>
              <h6 className="card-subtitle mb-2 text-muted small">
                Authorized Units
              </h6>
              <h4 className="card-title mb-0 fs-5 fs-md-4 text-info fw-bold">
                {formatFloat(
                  auths
                    .filter((a) => !a.complete)
                    .reduce(
                      (sum, a) =>
                        sum + (parseFloat(a.authorization_hours) || 0),
                      0
                    )
                )}
              </h4>
              <small
                className="text-muted d-block"
                style={{ fontSize: "0.7rem" }}
              >
                Active only
              </small>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="card h-100 border-warning">
            <div className="card-body text-center py-3">
              <div className="text-warning mb-2">
                <i className="bi bi-hourglass-split fs-2"></i>
              </div>
              <h6 className="card-subtitle mb-2 text-muted small">
                Hours Used
              </h6>
              <h4 className="card-title mb-0 fs-5 fs-md-4 text-warning fw-bold">
                {formatFloat(
                  auths
                    .filter((a) => !a.complete)
                    .reduce(
                      (sum, a) => sum + (parseFloat(a.total_hours) || 0),
                      0
                    )
                )}
              </h4>
              <small
                className="text-muted d-block"
                style={{ fontSize: "0.7rem" }}
              >
                Active only
              </small>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="card h-100 border-success">
            <div className="card-body text-center py-3">
              <div className="text-success mb-2">
                <i className="bi bi-speedometer2 fs-2"></i>
              </div>
              <h6 className="card-subtitle mb-2 text-muted small">
                Miles Used
              </h6>
              <h4 className="card-title mb-0 fs-5 fs-md-4 text-success fw-bold">
                {formatFloat(
                  auths
                    .filter((a) => !a.complete)
                    .reduce(
                      (sum, a) => sum + (parseFloat(a.total_miles) || 0),
                      0
                    )
                )}
              </h4>
              <small
                className="text-muted d-block"
                style={{ fontSize: "0.7rem" }}
              >
                Active only
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop/tablet table view */}
      <div className="d-none d-lg-block">
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-dark">
              <tr>
                <th>Auth Number</th>
                <th>Service</th>
                <th>Authorized "Units"</th>
                <th>Hours Used</th>
                <th>Miles Used</th>
                <th>Utilization</th>
                <th>Last Service</th>
                {role !== "job coach" && (
                  <th className="text-center">Documents</th>
                )}
              </tr>
            </thead>
            <tbody>
              {auths.map((a) => {
                const utilization = calculateUtilization(a);
                return (
                  <tr key={a.authorization_id}>
                    <td>
                      <strong className="text-primary">
                        {a.authorization_number}
                      </strong>
                      {a.auth_type && (
                        <>
                          <br />
                          <small className="text-muted text-capitalize">
                            {a.auth_type}
                          </small>
                        </>
                      )}
                    </td>
                    <td>
                      {a.service_name}
                      {a.flat_rate && <span className="text-muted"> (#)</span>}
                    </td>
                    <td>{formatFloat(a.authorization_hours)}</td>
                    <td>{formatFloat(a.total_hours)}</td>
                    <td>{formatFloat(a.total_miles)}</td>
                    <td className={getUtilizationColor(utilization)}>
                      <strong>{formatUtilization(utilization)}%</strong>
                      {a.flat_rate && (
                        <span className="badge bg-info ms-1" style={{ fontSize: "0.65rem" }} title="Flat rate - utilization capped at 100%">
                          Flat
                        </span>
                      )}
                      {a.auth_type === "mileage" && (
                        <small className="d-block text-muted">
                          ({formatFloat(a.total_miles)} /{" "}
                          {formatFloat(a.authorization_hours)} miles)
                        </small>
                      )}
                      {a.auth_type !== "mileage" && (
                        <small className="d-block text-muted">
                          ({formatFloat(a.total_hours)} /{" "}
                          {formatFloat(a.authorization_hours)} hours)
                        </small>
                      )}
                    </td>
                    <td>
                      <div>
                        <small className="fw-bold">
                          {formatDate(a.last_service_date)}
                        </small>
                        <br />
                        <small className="text-muted">
                          {a.service_count || 0} services
                        </small>

                        {a.complete && (
                          <>
                            <br />
                            <span
                              className="badge bg-success"
                              style={{ fontSize: "0.7rem" }}
                            >
                              <i className="bi bi-check-circle me-1"></i>
                              Complete
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    {role !== "job coach" && (
                      <td className="text-center">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() =>
                            handleViewDocs(
                              a.authorization_id,
                              a.authorization_number
                            )
                          }
                          title="View Documents (HIPAA Monitored)"
                        >
                          <i className="bi bi-folder-open me-1"></i>
                          <span className="d-none d-xl-inline">View Docs</span>
                          <span className="d-inline d-xl-none">Docs</span>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Tablet view - Improved card layout for medium screens */}
      <div className="d-none d-md-block d-lg-none">
        <div className="row g-3">
          {auths.map((a) => {
            const utilization = calculateUtilization(a);
            return (
              <div key={a.authorization_id} className="col-md-6">
                <div className="card h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="card-title mb-0 text-primary fw-bold">
                        {a.authorization_number}
                      </h6>
                      <span
                        className={`badge bg-secondary ${getUtilizationColor(
                          utilization
                        ).replace("text-", "bg-")}`}
                      >
                        {formatUtilization(utilization)}%
                      </span>
                    </div>
                    {a.auth_type && (
                      <small className="badge bg-secondary text-uppercase mb-2">
                        {a.auth_type}
                      </small>
                    )}
                    <p className="card-text text-muted mb-3">
                      {a.service_name}
                      {a.flat_rate && <span> (#)</span>}
                    </p>

                    <div className="row g-2 mb-3">
                      <div className="col-4">
                        <div className="text-center">
                          <div className="small text-muted">Authorized</div>
                          <div className="fw-bold">
                            {formatFloat(a.authorization_hours)}
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="text-center">
                          <div className="small text-muted">Hours Used</div>
                          <div className="fw-bold">
                            {formatFloat(a.total_hours)}
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="text-center">
                          <div className="small text-muted">Miles Used</div>
                          <div className="fw-bold">
                            {formatFloat(a.total_miles)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Added Last Service section for tablet */}
                    <div className="mb-3 p-2 bg-light rounded">
                      <div className="small text-muted">Last Service</div>
                      <div className="fw-bold small">
                        {formatDate(a.last_service_date)}
                      </div>
                      <div className="small text-muted">
                        {a.service_count || 0} services total
                      </div>
                      {a.complete && (
                        <div className="mt-1">
                          <span
                            className="badge bg-success"
                            style={{ fontSize: "0.7rem" }}
                          >
                            <i className="bi bi-check-circle me-1"></i>
                            Complete
                          </span>
                        </div>
                      )}
                    </div>

                    {!isJobCoach && (
                      <button
                        className="btn btn-outline-primary btn-sm w-100"
                        onClick={() =>
                          handleViewDocs(
                            a.authorization_id,
                            a.authorization_number
                          )
                        }
                        title="View Documents (HIPAA Monitored)"
                      >
                        <i className="bi bi-folder-open me-1"></i>
                        View Documents
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* âœ… Enhanced Mobile-friendly stacked list view */}
      <div className="d-block d-md-none">
        <div className="row g-2">
          {auths.map((a) => {
            const utilization = calculateUtilization(a);
            return (
              <div key={a.authorization_id} className="col-12">
                <div className="card">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="mb-1 text-primary fw-bold fs-6">
                          {a.authorization_number}
                        </h6>
                        {a.auth_type && (
                          <small className="badge bg-secondary text-uppercase">
                            {a.auth_type}
                          </small>
                        )}
                      </div>
                      <span
                        className={`badge fs-6 ${getUtilizationColor(
                          utilization
                        ).replace("text-", "bg-")}`}
                      >
                        {formatUtilization(utilization)}%
                      </span>
                    </div>

                    <p className="mb-2 text-muted small">
                      {a.service_name}
                      {a.flat_rate && <span> (#)</span>}
                    </p>

                    <div className="row g-2 text-center mb-3">
                      <div className="col-4">
                        <div className="bg-light p-2 rounded">
                          <div className="small text-muted">Authorized</div>
                          <div className="fw-bold small">
                            {formatFloat(a.authorization_hours)}
                            <small className="text-muted d-block">
                              {a.auth_type === "mileage" ? "miles" : "hours"}
                            </small>
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="bg-light p-2 rounded">
                          <div className="small text-muted">Hours Used</div>
                          <div className="fw-bold small">
                            {formatFloat(a.total_hours)}
                            <small className="text-muted d-block">hours</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="bg-light p-2 rounded">
                          <div className="small text-muted">Miles Used</div>
                          <div className="fw-bold small">
                            {formatFloat(a.total_miles)}
                            <small className="text-muted d-block">miles</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Utilization details for mobile */}
                    <div className="small text-center mb-3">
                      {a.auth_type === "mileage" ? (
                        <span className={getUtilizationColor(utilization)}>
                          <strong>{formatUtilization(utilization)}%</strong>{" "}
                          utilized ({formatFloat(a.total_miles)} /{" "}
                          {formatFloat(a.authorization_hours)} miles)
                        </span>
                      ) : (
                        <span className={getUtilizationColor(utilization)}>
                          <strong>{formatUtilization(utilization)}%</strong>{" "}
                          utilized ({formatFloat(a.total_hours)} /{" "}
                          {formatFloat(a.authorization_hours)} hours)
                          {a.flat_rate && (
                            <span className="badge bg-info ms-1" style={{ fontSize: "0.6rem" }}>Flat</span>
                          )}
                        </span>
                      )}
                    </div>

                    {/* Added Last Service section for mobile */}
                    <div className="mb-3 p-2 bg-light rounded text-center">
                      <div className="small text-muted">Last Service</div>
                      <div className="fw-bold small">
                        {formatDate(a.last_service_date)}
                      </div>
                      <div className="small text-muted">
                        {a.service_count || 0} services
                      </div>
                      {a.complete && (
                        <div className="mt-1">
                          <span
                            className="badge bg-success"
                            style={{ fontSize: "0.7rem" }}
                          >
                            <i className="bi bi-check-circle me-1"></i>
                            Complete
                          </span>
                        </div>
                      )}
                    </div>

                    {!isJobCoach && (
                      <button
                        className="btn btn-primary btn-sm w-100"
                        onClick={() =>
                          handleViewDocs(
                            a.authorization_id,
                            a.authorization_number
                          )
                        }
                        title="View Documents (HIPAA Monitored)"
                      >
                        <i className="bi bi-folder-open me-1"></i>
                        View Documents
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {auths.length === 0 && (
        <div className="alert alert-info mt-3 text-center">
          <i className="bi bi-info-circle me-2"></i>
          No authorizations found for this consumer.
        </div>
      )}
      {/* Customer-Vendor Relationship Notice */}
      {tenantType === "customer" && auths.length > 0 && (
        <div className="mt-4 p-3 bg-light border-start border-primary border-4">
          <h6 className="text-primary fs-6">
            <i className="bi bi-building me-2"></i>
            Customer-Vendor Access Model
          </h6>
          <p className="mb-0 small">
            As a customer tenant user, you can view documents and data for your
            consumers even when the work is being performed by vendor tenants.
            All cross-tenant access is HIPAA-compliant and fully audited.
          </p>
        </div>
      )}
    </div>
  );
};

export default MyConsumerAuth;